import React, { useMemo, useState, useCallback } from 'react'
import { AnalysisSidebarProps } from './AnalysisSidebar.types'
import {
  SidebarWrapper,
  SidebarContainer,
  SidebarToggleButton,
  MobileOverlay,
  SidebarContent,
  NavSection,
  NavTitle,
  NavItem,
  NavIcon,
  // NavFilterDot,
  NavCount,
} from './AnalysisSidebar.styles'
import { Icon } from '../../atoms/Icon'

export const AnalysisSidebar: React.FC<AnalysisSidebarProps> = ({
  sections,
  activeSection,
  onSectionChange,
  className,
  footer,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Flat list of all item IDs for keyboard navigation
  const allItemIds = useMemo(
    () => sections.flatMap((s) => s.items.map((i) => i.id)),
    [sections]
  )

  const handleSectionChange = (sectionId: string) => {
    onSectionChange(sectionId)
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 1024) {
      setIsSidebarOpen(false)
    }
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, itemId: string) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      e.preventDefault()

      const idx = allItemIds.indexOf(itemId)
      if (idx === -1) return

      const next =
        e.key === 'ArrowDown'
          ? (idx + 1) % allItemIds.length
          : (idx - 1 + allItemIds.length) % allItemIds.length
      const nextId = allItemIds[next]

      handleSectionChange(nextId)

      // Focus the next NavItem
      const el = document.querySelector(
        `[data-section-id="${nextId}"]`
      ) as HTMLElement
      el?.focus()
    },
    [allItemIds, handleSectionChange]
  )

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  return (
    <>
      <SidebarWrapper className={className}>
        <SidebarContainer $isOpen={isSidebarOpen}>
          <SidebarContent role="navigation">
            {sections.map((section) => (
              <NavSection key={section.title}>
                <NavTitle $sectionColor={section.color}>
                  {section.title}
                </NavTitle>
                {section.items.map((item) => (
                  <NavItem
                    key={item.id}
                    tabIndex={0}
                    data-section-id={item.id}
                    $active={activeSection === item.id}
                    $sectionColor={section.color}
                    onClick={() => handleSectionChange(item.id)}
                    onKeyDown={(e) => handleKeyDown(e, item.id)}
                  >
                    <NavIcon
                      $active={activeSection === item.id}
                      $sectionColor={section.color}
                    >
                      <Icon name={item.icon} size="xs" />
                    </NavIcon>
                    {item.label}
                    {item.count != null && item.count > 0 && (
                      <NavCount>{item.count}</NavCount>
                    )}
                    {/* {item.hasActiveFilters && !item.count && <NavFilterDot />} */}
                  </NavItem>
                ))}
              </NavSection>
            ))}
          </SidebarContent>
          {footer}
        </SidebarContainer>

        <SidebarToggleButton $isOpen={isSidebarOpen} onClick={toggleSidebar}>
          <Icon name="grid" size="small" color="#2B3F6C" />
        </SidebarToggleButton>
      </SidebarWrapper>

      <MobileOverlay
        $isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(false)}
      />
    </>
  )
}

// Export styled components for custom styling if needed
export {
  SidebarWrapper,
  SidebarContainer,
  SidebarToggleButton,
  MobileOverlay,
  SidebarContent,
  NavSection,
  NavTitle,
  NavItem,
  NavIcon,
  NavFilterDot,
  NavCount,
} from './AnalysisSidebar.styles'
